import React from 'react'
import {
    Box,
    SimpleGrid,
    Text,
    Textarea,
    useDisclosure,
} from '@chakra-ui/react'

import SharedSelectableOption from '~components/shared/wrappers/shared-selectable-option'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedHelpText from '~components/shared/form/shared-help-text'
import CopyTemplateModal from '~components/chats/chat/input/templates/modal'
import MessageTemplatesProvider from '~components/engagement/message-templates/provider'
import { useClinic } from '~contexts/clinic-context'
import {
    ClientPreviewFieldsFragment,
    PrescriptionRefillRequestFieldsFragment,
    PrescriptionRefillRequestType,
} from '~graphql/generated/graphql'

interface Props {
    refillRequest: PrescriptionRefillRequestFieldsFragment
    explanation: string
    setExplanation: (explanation: string) => void
}

const PrescriptionDenialReasonForm: React.FC<Props> = ({
    refillRequest,
    explanation,
    setExplanation,
}) => {
    const { clinic } = useClinic()
    const { isOpen, onOpen, onClose } = useDisclosure()

    const client = refillRequest.client as ClientPreviewFieldsFragment | null
    const patient = refillRequest.patient

    if (!clinic || !client || !patient) {
        return (
            <>
                There is an issue with the data on this refill request. Please
                contact support.
            </>
        )
    }

    const isRenewal =
        refillRequest.type === PrescriptionRefillRequestType.Renewal
    const requestTypeLabel = isRenewal ? 'renewal' : 'refill'

    const quickReasons: { label: string; reason: string; helpText: string }[] =
        [
            {
                label: 'Exam required',
                reason: `A current exam is required before this ${requestTypeLabel} can be approved. Please schedule an appointment: https://portal.chckup.app/book/${clinic?.slug}`,
                helpText: 'Quick Action',
            },
            {
                label: 'Medication unavailable',
                reason: `The requested medication is currently out of stock. We will contact you when it is available or discuss alternatives.`,
                helpText: 'Quick Action',
            },
            {
                label: isRenewal ? 'No longer prescribed' : 'Out of refills',
                reason: isRenewal
                    ? `This medication is no longer prescribed for your pet. Please contact us to discuss next steps.`
                    : `This prescription has no remaining refills and requires a renewal from the prescribing veterinarian.`,
                helpText: 'Quick Action',
            },
        ]

    return (
        <MessageTemplatesProvider clinic={clinic} isOpen={isOpen}>
            <Box>
                <Text fontWeight="semibold" mb={1}>
                    Why are you denying this {requestTypeLabel} request?
                </Text>
                <SharedHelpText>
                    Clients will be notified through SMS, email, and the app (if
                    applicable).
                </SharedHelpText>
                <SimpleGrid columns={2} mt={2} gridGap={4} alignItems="stretch">
                    {quickReasons.map(({ label, reason, helpText }) => (
                        <SharedSelectableOption
                            key={label}
                            isSelected={explanation === reason}
                            onClick={() => setExplanation(reason)}
                            label={label}
                            helpText={helpText}
                            flex={1}
                        />
                    ))}
                    <SharedSelectableOption
                        isSelected={false}
                        onClick={() => onOpen()}
                        label="Message Template"
                        helpText="Select from a pre-written message template."
                        flex={1}
                    />
                </SimpleGrid>
                <Textarea
                    rounded="xl"
                    mt={6}
                    value={explanation}
                    onChange={e => setExplanation(e.target.value)}
                    focusBorderColor="brand.500"
                    placeholder="Notes..."
                    minH="100px"
                />
                <CopyTemplateModal
                    isOpen={isOpen}
                    onClose={onClose}
                    clinic={clinic}
                    client={client}
                    patient={patient}
                    setText={setExplanation}
                />
            </Box>
        </MessageTemplatesProvider>
    )
}

export default PrescriptionDenialReasonForm
